// Host global da monetização. Montado UMA vez, no AppRoutes, para existir
// também fora da PlataformaPessoal (a tela de resultado do ScanFood é rota
// própria). Precisa ficar DENTRO do <Router>: a tela de planos usa useNavigate.
//
// Faz três coisas:
//   1. mantém o serviço (monetizacao.js) sabendo quem está logado e se o tier
//      atual vê anúncio — SEM listener novo: o tier sai do mesmo USER que o
//      UserContext já escuta (VINCULADO vem do servidor, em getEstadoAnuncios);
//   2. carrega a config de anúncios uma vez por sessão;
//   3. desenha o funil do Premium quando alguém pede (abrirFunilPremium). As
//      telas e a ordem vivem no FunilPremium:
//        inicio 'oferta'       oferta → lembrete → comparativo → planos
//                              (pós-intersticial)
//        inicio 'comparativo'  comparativo → lembrete → planos
//                              (botões e telas do app — ver premium/abrirPremium)

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { featureLiberada, resolverPerfilDeAcesso } from '../../utils/entitlements';
import { carregarEstadoAnuncios, esquecerEstadoAnuncios } from './anunciosApi';
import { configurarMonetizacao, registrarHost } from './monetizacao';
import { ligarDeduplicacao } from './lembreteLocal';
import FunilPremium, { carregarContextoFunil } from '../premium/FunilPremium';

export default function MonetizacaoHost() {
  const { currentUser, user } = useUser() || {};
  const uid = currentUser?.uid || null;

  // Tier efetivo com o vínculo de academia (USER.PARCEIRO): vinculado a
  // academia Essencial vê anúncio, e a academia pode desligar (BENEFICIOS.ADS).
  const perfil = resolverPerfilDeAcesso({
    subscriptionInfo: user?.SUBSCRIPTION_INFO || {},
    criador: user?.CRIADOR || null,
    parceiro: user?.PARCEIRO || null,
  });
  const podeAdsLocal = featureLiberada('ads', perfil.tier, perfil.beneficios);

  useEffect(() => {
    configurarMonetizacao({ uid, podeAds: podeAdsLocal });
  }, [uid, podeAdsLocal]);

  useEffect(() => {
    if (!uid) {
      esquecerEstadoAnuncios();
      return;
    }
    carregarEstadoAnuncios(uid);
    ligarDeduplicacao();
  }, [uid]);

  // ── Funil ────────────────────────────────────────────────────────────────
  const [funil, setFunil] = useState(null); // {id, inicio, origem, contexto}
  const resolverRef = useRef(null);
  // Entre o toque e a primeira tela há uma ida ao servidor (config + loja):
  // um segundo toque nesse meio-tempo não abre um segundo funil.
  const abrindoRef = useRef(false);

  const fechar = useCallback(() => {
    setFunil(null);
    const r = resolverRef.current;
    resolverRef.current = null;
    r?.();
  }, []);

  useEffect(() => registrarHost({
    abrirFunil: async ({ origem, inicio = 'oferta' } = {}) => {
      if (resolverRef.current || abrindoRef.current) return undefined; // já tem um funil
      abrindoRef.current = true;
      let contexto;
      try {
        contexto = await carregarContextoFunil();
      } catch {
        contexto = null; // FunilPremium assume sem teste e sem lembrete
      } finally {
        abrindoRef.current = false;
      }
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setFunil({ id: Date.now(), inicio, origem, contexto });
      });
    },
  }), []);

  if (!funil) return null;

  return (
    <FunilPremium
      key={funil.id}
      inicio={funil.inicio}
      origem={funil.origem}
      contexto={funil.contexto}
      onFim={fechar}
    />
  );
}
